import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

interface AudioStateContextType {
  isPlaying: boolean;
  activeArticleId: string | null;
  setAudioState: (playing: boolean, articleId: string | null) => void;
}

const AudioStateContext = createContext<AudioStateContextType | undefined>(undefined);

export const useAudioState = () => {
  const context = useContext(AudioStateContext);
  if (context === undefined) {
    throw new Error('useAudioState must be used within AudioStateProvider');
  }
  return context;
};

export const AudioStateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);

  const setAudioState = useCallback((playing: boolean, articleId: string | null) => {
    setIsPlaying(playing);
    setActiveArticleId(articleId);
  }, []);

  const value = useMemo(
    () => ({ isPlaying, activeArticleId, setAudioState }),
    [isPlaying, activeArticleId, setAudioState],
  );

  return (
    <AudioStateContext.Provider value={value}>
      {children}
    </AudioStateContext.Provider>
  );
};
